import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faClipboardCheck,
  faClock,
  faPaperPlane,
  faPaperclip,
  faPlus,
  faSpinner,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../components/Ui_Element/CustomToast";
import config from "../../config";
import { getById, post } from "../../contexts/apicontexts";
import TaskComposerModal from "../Tasks/TasksModels/TaskComposerModal";
import { createTask, fetchEmployees, getTaskList } from "../Tasks/taskService";
import {
  formatTaskDate,
  getTaskForwardCount,
  getTaskPriorityClasses,
  getTaskStatusClasses,
  isTaskForwardLocked,
  isTaskFullyCompleted,
} from "../Tasks/taskUtils";
import {
  buildMediaUrl,
  formatDateTime,
  getCurrentUserIdentity,
  getIdentityRoleLabel,
  getPriorityClasses,
  getStatusClasses,
  isCustomerUserIdentity,
  isCustomerIdentity,
  normalizeTicketData,
} from "./ticketUtils";

const statusOptions = ["New", "In Progress", "On Hold", "Resolved"];

export default function TicketDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const identityRoleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);
  const isCustomerUserView = useMemo(() => isCustomerUserIdentity(identity), [identity]);
  const backTo = location.state?.backTo || "/tickets";
  const forceViewOnly = Boolean(location.state?.viewOnly);
  const backLabel = String(backTo).startsWith("/assigned-tickets")
    ? "Back to Assigned Tickets"
    : String(backTo).startsWith("/task-history")
      ? "Back to Task History"
      : String(backTo).startsWith("/all-tasks")
        ? "Back to All Task"
    : String(backTo).startsWith("/my-tasks")
      ? "Back to My Tasks"
      : String(backTo).startsWith("/tasks")
        ? "Back to Tasks"
      : "Back to Tickets";

  const [ticket, setTicket] = useState(null);
  const [linkedTasks, setLinkedTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [error, setError] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [replyStatus, setReplyStatus] = useState("");
  const [replyFile, setReplyFile] = useState(null);
  const [isTaskOpen, setIsTaskOpen] = useState(false);
  const canReplyToTicket = Boolean(ticket?.canReply) && !forceViewOnly;
  const canManageTicketStatus = Boolean(ticket?.canManageStatus) && !forceViewOnly;

  const fetchTicket = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getById(`${config.baseurl}Ticket/getById/${id}`);
      if (response?.status === 200) {
        const normalized = normalizeTicketData(response.data);
        setTicket(normalized);
        setReplyStatus(normalized?.status || "");
      } else {
        setError("Failed to load ticket details.");
      }
    } catch {
      setError("Failed to load ticket details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    if (isCustomerIdentity(identity)) return;

    let cancelled = false;

    const loadTaskData = async () => {
      const [taskItems, employeeItems] = await Promise.all([getTaskList({ ticketId: Number(id) }), fetchEmployees()]);
      if (cancelled) return;
      setLinkedTasks(taskItems);
      setEmployees(employeeItems);
    };

    loadTaskData();

    return () => {
      cancelled = true;
    };
  }, [id, identity]);

  const handleReply = async () => {
    if (!canReplyToTicket) {
      setError("This ticket is view only for your login.");
      return;
    }

    const trimmed = replyMessage.trim();
    const statusChanged = canManageTicketStatus && replyStatus && replyStatus !== ticket?.status ? replyStatus : "";

    if (!trimmed && !replyFile && !statusChanged) {
      setError("Add a reply, attachment, or status change before submitting.");
      return;
    }

    if (statusChanged === "Resolved" && !isCustomerIdentity(identity)) {
      try {
        const latestLinkedTasks = await getTaskList({ ticketId: Number(id) });
        setLinkedTasks(latestLinkedTasks);

        const hasLinkedTasks = latestLinkedTasks.length > 0;
        const incompleteTasks = latestLinkedTasks.filter((task) => !isTaskFullyCompleted(task));

        if (hasLinkedTasks && incompleteTasks.length > 0) {
          const message = `This ticket cannot be resolved yet. ${incompleteTasks.length} linked ${
            incompleteTasks.length === 1 ? "task is" : "tasks are"
          } not completed at 100% progress.`;
          setError("");
          showToast.error(message);
          return;
        }
      } catch {
        const message = "Linked tasks could not be verified. Please try again before resolving the ticket.";
        setError("");
        showToast.error(message);
        return;
      }
    }

    setSaving(true);
    setError("");

    const formData = new FormData();
    formData.append("TicketId", String(id));
    if (trimmed) formData.append("Message", trimmed);
    if (statusChanged) formData.append("Status", statusChanged);
    if (replyFile) formData.append("AttachmentFile", replyFile);

    try {
      const response = await post(`${config.baseurl}Ticket/reply`, formData);

      if (response?.status === 200) {
        const normalized = normalizeTicketData(response.data);
        setTicket(normalized);
        setReplyMessage("");
        setReplyFile(null);
        setReplyStatus(normalized?.status || "");
        return;
      }

      setError(response?.message || "Failed to send reply.");
    } catch {
      setError("Failed to send reply.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTask = async (values) => {
    if (!ticket) return;

    setTaskSaving(true);
    setError("");

    try {
      await createTask(
        {
          ...values,
          ticketId: Number(ticket.id || 0),
          ticketNumber: ticket.ticketNumber || "",
          ticketSubject: ticket.subject || "",
          ticketStatus: ticket.status || "",
          sourceType: "SupportTicket",
          createdByUserId: identity.userId,
          createdByName: identity.fullName || identity.email || "User",
        },
        identity
      );

      setIsTaskOpen(false);
      setLinkedTasks(await getTaskList({ ticketId: Number(id) }));
    } catch {
      setError("Task could not be created from this ticket.");
    } finally {
      setTaskSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <Breadcrumb pageName="Ticket Details" currentPage="Ticket Details" />

      <div className="mt-4 flex items-center">
        <Link
          to={backTo}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {backLabel}
        </Link>
      </div>

      {error ? <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      {loading ? (
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500 shadow-sm">
          <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
          Loading ticket details...
        </div>
      ) : !ticket ? (
        <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white">
            <FontAwesomeIcon icon={faTicket} />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Ticket not found</h2>
          <p className="mt-2 text-sm text-slate-500">The ticket may not exist or you may not have permission to view it.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,2fr)_380px]">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#0f172a,#1e293b)] px-6 py-6 text-white">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                    <span>{ticket.ticketNumber}</span>
                    <span className="h-1 w-1 rounded-full bg-slate-400" />
                    <span>{ticket.category}</span>
                  </div>
                  <h1 className="text-2xl font-semibold">{ticket.subject}</h1>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(ticket.status)}`}>{ticket.status}</span>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getPriorityClasses(ticket.priority)}`}>{ticket.priority}</span>
                  </div>
                </div>

                <div className="text-sm text-slate-200">
                  <div>Opened {formatDateTime(ticket.createdAt)}</div>
                  <div className="mt-1">Last activity {formatDateTime(ticket.lastReplyAt || ticket.createdAt)}</div>
                  <div className="mt-3 inline-flex items-center rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-100">
                    {forceViewOnly ? "Task list view only" : canManageTicketStatus ? "Staff ticket view" : canReplyToTicket ? `${identityRoleLabel} reply access` : `${identityRoleLabel} view only`}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4 bg-slate-50 p-5 md:p-6">
              {(ticket.conversation || []).map((item) => {
                const isMine = Boolean(item.userId) && String(item.userId) === String(identity.userId);

                return (
                  <div key={`${item.id}-${item.createdAt}`} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={[
                        "max-w-3xl rounded-3xl border px-4 py-4 shadow-sm",
                        isMine
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-900",
                      ].join(" ")}
                    >
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className={`rounded-full px-2.5 py-1 font-semibold ${isMine ? "bg-white/10 text-white" : "bg-slate-100 text-slate-700"}`}>
                          {item.senderType}
                        </span>
                        {item.status ? (
                          <span className={`rounded-full px-2.5 py-1 font-semibold ${getStatusClasses(item.status)}`}>
                            Status: {item.status}
                          </span>
                        ) : null}
                        {item.isInitialMessage ? (
                          <span className={`rounded-full px-2.5 py-1 font-semibold ${isMine ? "bg-white/10 text-white" : "bg-amber-100 text-amber-700"}`}>
                            Ticket opened
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-3 flex items-start justify-between gap-4">
                        <div>
                          <div className={`text-sm font-semibold ${isMine ? "text-white" : "text-slate-900"}`}>{item.senderName}</div>
                          <div className={`mt-1 text-xs ${isMine ? "text-slate-300" : "text-slate-500"}`}>{formatDateTime(item.createdAt)}</div>
                        </div>
                        {item.senderEmail ? (
                          <div className={`text-xs ${isMine ? "text-slate-300" : "text-slate-500"}`}>{item.senderEmail}</div>
                        ) : null}
                      </div>

                      {item.message ? (
                        <p className={`mt-4 whitespace-pre-wrap text-sm leading-7 ${isMine ? "text-slate-100" : "text-slate-700"}`}>
                          {item.message}
                        </p>
                      ) : null}

                      {item.attachmentUrl ? (
                        <a
                          href={buildMediaUrl(item.attachmentUrl)}
                          target="_blank"
                          rel="noreferrer"
                          className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
                            isMine ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          }`}
                        >
                          <FontAwesomeIcon icon={faPaperclip} />
                          View attachment
                        </a>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Ticket Info</h2>
              <div className="mt-4 space-y-3 text-sm">
                <InfoRow label="Organization" value={ticket.organizationName || "Not provided"} />
                <InfoRow label="Customer" value={ticket.customerName || "Not provided"} />
                <InfoRow label="Customer Code" value={ticket.customerCode || "Not provided"} />
                <InfoRow label="Requester" value={ticket.requesterName || ticket.userName} />
                <InfoRow label="Requester Email" value={ticket.requesterEmail || ticket.contactEmail} />
                <InfoRow label="Contact Email" value={ticket.contactEmail} />
                <InfoRow label="Domain URL" value={ticket.domainUrl || "Not provided"} />
                <InfoRow label="Requester User ID" value={ticket.requesterUserId || ticket.userId} mono />
                <InfoRow label="Updated" value={formatDateTime(ticket.lastReplyAt || ticket.createdAt)} />
              </div>
            </div>

            {!isCustomerIdentity(identity) && (
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Linked Tasks</h2>
                    <p className="mt-1 text-sm text-slate-500">Create internal work items and track ownership for this ticket.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm(`Create a task from ticket ${ticket.ticketNumber || ""}?`)) return;
                      setIsTaskOpen(true);
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Make Task
                  </button>
                </div>

                {linkedTasks.length ? (
                  <div className="mt-5 space-y-3">
                    {linkedTasks.map((task) => (
                      <div key={task.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskStatusClasses(task.status)}`}>
                            {task.status}
                          </span>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskPriorityClasses(task.priority)}`}>
                            {task.priority}
                          </span>
                        </div>
                        <div className="mt-3 text-sm font-semibold text-slate-900">{task.title}</div>
                        <div className="mt-1 text-xs text-slate-500">Assigned to {task.assignedEmployeeName || "Unassigned"}</div>
                        <div className="mt-3 flex flex-wrap gap-2 text-xs">
                          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-600">
                            {getTaskForwardCount(task)} {getTaskForwardCount(task) === 1 ? "forward" : "forwards"}
                          </span>
                          <span className={`rounded-full px-2.5 py-1 font-semibold ${isTaskForwardLocked(task) ? "border border-amber-200 bg-amber-100 text-amber-700" : "border border-emerald-200 bg-emerald-100 text-emerald-700"}`}>
                            {isTaskForwardLocked(task) ? "Forward locked" : "Current owner active"}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-slate-400">Due {formatTaskDate(task.dueDate)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-5 rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                    <FontAwesomeIcon icon={faClipboardCheck} className="mr-2" />
                    No task has been created from this ticket yet.
                  </div>
                )}
              </div>
            )}

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Add Reply</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {canReplyToTicket
                      ? "Continue the conversation or update the status."
                      : "This ticket is visible to you, but replies are disabled for this login."}
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  <FontAwesomeIcon icon={faClock} />
                  {forceViewOnly ? "Task view only" : canManageTicketStatus ? "Staff view" : canReplyToTicket ? "Reply allowed" : "View only"}
                </div>
              </div>

              {canReplyToTicket ? (
                <div className="mt-5 space-y-4">
                  {canManageTicketStatus ? (
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Status</label>
                      <select
                        value={replyStatus}
                        onChange={(event) => setReplyStatus(event.target.value)}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                      >
                        {statusOptions.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}

                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Reply</label>
                    <textarea
                      value={replyMessage}
                      onChange={(event) => setReplyMessage(event.target.value)}
                      className="min-h-[140px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-slate-400"
                      placeholder="Write your update, answer, or follow-up message..."
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Attachment</label>
                    <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 transition hover:border-slate-400 hover:bg-slate-100">
                      <FontAwesomeIcon icon={faPaperclip} className="text-slate-500" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-700">
                          {replyFile ? replyFile.name : "Attach supporting file"}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">Optional for reply or status update</div>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(event) => setReplyFile(event.target.files?.[0] || null)}
                      />
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={handleReply}
                    disabled={saving}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-70"
                  >
                    <FontAwesomeIcon icon={saving ? faSpinner : faPaperPlane} spin={saving} />
                    {saving ? "Sending..." : "Add Reply"}
                  </button>
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                  {forceViewOnly
                    ? "This ticket was opened from the task list in view-only mode. You can review the full conversation here, but replying is disabled from this entry point."
                    : isCustomerUserView
                    ? "This ticket was created by another user under your customer account. You can review the history here, but only the original requester or support staff can reply."
                    : "Replies are not available for this ticket on your current access level."}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <TaskComposerModal
        open={isTaskOpen}
        ticket={ticket}
        employees={employees}
        identity={identity}
        submitting={taskSaving}
        onClose={() => setIsTaskOpen(false)}
        onSubmit={handleCreateTask}
      />
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right font-medium text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}
